/**
 * SearchChip — header search backed by /api/v1/search/global.
 *
 * Popover (not modal) with debounced backend search, mixed-type results,
 * grouped by project, keyboard navigation (↑↓ Enter Esc). Listens for the
 * `eis:open-search` CustomEvent so a global Ctrl/⌘+F handler in App.js can
 * open it from anywhere.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Article,
  Assignment,
  Bookmark,
  Build,
  Folder,
  Inventory,
  Search,
} from '@mui/icons-material';
import { api } from '../../../services/api';

const DEBOUNCE_MS = 250;
const MIN_QUERY = 2;

const TYPE_ICONS = {
  document: <Article fontSize="small" />,
  requirement: <Assignment fontSize="small" />,
  cad_drawing: <Build fontSize="small" />,
  compliance_standard: <Bookmark fontSize="small" />,
  bom_line: <Inventory fontSize="small" />,
  project: <Folder fontSize="small" />,
};

const TYPE_LABELS = {
  document: 'Document',
  requirement: 'Requirement',
  cad_drawing: 'Drawing',
  compliance_standard: 'Standard',
  bom_line: 'BOM line',
  project: 'Project',
};

const SearchChip = ({ onPick }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const chipRef = useRef(null);
  const inputRef = useRef(null);

  const isOpen = Boolean(anchorEl);

  const open = useCallback((e) => {
    setAnchorEl(e?.currentTarget || chipRef.current);
  }, []);

  const close = useCallback(() => {
    setAnchorEl(null);
    setQuery('');
    setResults([]);
    setTruncated(false);
    setSelectedIndex(0);
  }, []);

  // Global Ctrl/⌘+F event from App.js — open the popover anchored on the chip.
  useEffect(() => {
    const handler = () => open();
    window.addEventListener('eis:open-search', handler);
    return () => window.removeEventListener('eis:open-search', handler);
  }, [open]);

  // Debounced search.
  useEffect(() => {
    if (!isOpen) return undefined;
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY) {
      setResults([]);
      setTruncated(false);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const id = setTimeout(() => {
      let cancelled = false;
      api
        .globalSearch({ q: trimmed, limit: 50 })
        .then((res) => {
          if (cancelled) return;
          const list = Array.isArray(res?.data?.results) ? res.data.results : [];
          setResults(list);
          setTruncated(!!res?.data?.truncated);
          setSelectedIndex(0);
        })
        .catch(() => {
          if (!cancelled) {
            setResults([]);
            setTruncated(false);
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }, DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query, isOpen]);

  const handleKeyDown = (e) => {
    if (!results.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = results[selectedIndex];
      if (hit) {
        onPick?.(hit);
        close();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };

  const pick = (hit) => {
    onPick?.(hit);
    close();
  };

  const trimmedLen = query.trim().length;
  const showHint = trimmedLen < MIN_QUERY;
  const showNoMatches = !loading && !showHint && results.length === 0;

  const grouped = useMemo(() => {
    const out = [];
    let lastProject = null;
    results.forEach((r, idx) => {
      const projectKey = r.project_name || r.project_id || '—';
      if (projectKey !== lastProject) {
        out.push({ kind: 'header', label: projectKey });
        lastProject = projectKey;
      }
      out.push({ kind: 'hit', hit: r, idx });
    });
    return out;
  }, [results]);

  return (
    <>
      <Tooltip title="Search (Ctrl/⌘ + F)">
        <Box
          ref={chipRef}
          onClick={open}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.5,
            py: 0.5,
            minWidth: 260,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '20px',
            cursor: 'pointer',
            bgcolor: 'action.hover',
            transition: 'border-color 0.15s ease, background-color 0.15s ease',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'rgba(102, 126, 234, 0.06)',
            },
          }}
        >
          <Search sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem', flexGrow: 1 }}>
            Search…
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem', flexShrink: 0 }}>
            ⌘F
          </Typography>
        </Box>
      </Tooltip>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 520, maxHeight: 520 } } }}
      >
        <Box sx={{ p: 1.5 }}>
          <TextField
            inputRef={inputRef}
            autoFocus
            fullWidth
            size="small"
            placeholder="Search documents, drawings, requirements…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            InputProps={{ startAdornment: <Search sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} /> }}
          />
        </Box>
        <Divider />
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {showHint && (
            <Typography variant="caption" color="text.secondary" sx={{ p: 2, display: 'block' }}>
              Type at least {MIN_QUERY} characters to search.
            </Typography>
          )}
          {loading && (
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <CircularProgress size={18} />
            </Box>
          )}
          {showNoMatches && (
            <Typography variant="caption" color="text.secondary" sx={{ p: 2, display: 'block' }}>
              No matches.
            </Typography>
          )}
          {!loading && results.length > 0 && (
            <List dense disablePadding>
              {grouped.map((entry, gi) => {
                if (entry.kind === 'header') {
                  return (
                    <Box
                      key={`h-${gi}`}
                      sx={{
                        px: 2,
                        py: 0.5,
                        bgcolor: 'action.hover',
                        fontSize: '0.7rem',
                        color: 'text.secondary',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                      }}
                    >
                      {entry.label}
                    </Box>
                  );
                }
                const { hit, idx } = entry;
                const isSelected = idx === selectedIndex;
                return (
                  <ListItemButton
                    key={`${hit.entity_type}-${hit.entity_id}`}
                    selected={isSelected}
                    onClick={() => pick(hit)}
                    onMouseEnter={() => setSelectedIndex(idx)}
                    sx={{ py: 0.75 }}
                  >
                    <Box sx={{ mr: 1.25, color: 'text.secondary' }}>
                      {TYPE_ICONS[hit.entity_type] || <Article fontSize="small" />}
                    </Box>
                    <ListItemText
                      primary={hit.title}
                      secondary={hit.snippet}
                      primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
                      secondaryTypographyProps={{
                        fontSize: '0.75rem',
                        sx: {
                          display: '-webkit-box',
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        },
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {TYPE_LABELS[hit.entity_type] || hit.entity_type}
                    </Typography>
                  </ListItemButton>
                );
              })}
            </List>
          )}
          {!loading && truncated && (
            <Typography variant="caption" color="text.secondary" sx={{ p: 1.5, display: 'block', textAlign: 'center' }}>
              Showing the first results — refine your search to narrow it down.
            </Typography>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default SearchChip;
